/**
 * admin 페이지(window.opener) 또는 동일 origin의 다른 탭으로
 * 학습 활동 이벤트를 송신한다.
 *
 * - opener가 있으면 opener.postMessage
 * - 없으면 BroadcastChannel('semifab-progress')로도 보내서
 *   admin이 같은 브라우저에서 다른 탭일 때 수신 가능
 *
 * milestone 컨벤션: `day{N}:{module}:{event}`
 *   - 예) day1:vacuum:complete, day1:vacuum:quiz, day1:vacuum:troubleshoot
 *   - 예) day{N}:time
 */

const LICENSE_STORAGE_KEY = 'semi-license';
let bc = null;
function getBroadcastChannel() {
  if (bc !== null) return bc;
  try {
    bc = typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel('semifab-progress')
      : false;
  } catch {
    bc = false;
  }
  return bc;
}

function getLicense() {
  try {
    const raw = localStorage.getItem(LICENSE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * 임의 milestone 이벤트 송신.
 * @param {string} milestone - 예: 'day1:vacuum:quiz'
 * @param {object} [data]
 */
export function reportEvent(milestone, data = {}) {
  const license = getLicense();
  if (!license || !license.code) return; // 미인증 사용자는 보내지 않음

  const payload = {
    type: 'PROGRESS',
    code: license.code,
    cohortId: license.cohortId || null,
    studentSeq: license.studentSeq || null,
    milestone,
    data,
    at: new Date().toISOString(),
    source: 'semi-edu',
  };

  try {
    if (typeof window !== 'undefined' && window.opener && !window.opener.closed) {
      window.opener.postMessage(payload, '*');
    }
  } catch { /* 무시 */ }

  const ch = getBroadcastChannel();
  if (ch) {
    try { ch.postMessage(payload); } catch { /* 무시 */ }
  }

  // Firestore에 직접 쓰기 (admin 탭이 열려있지 않아도 유실되지 않음)
  writeToFirestore(payload).catch(() => { /* 조용히 실패 */ });
}

let firestoreWriterPromise = null;
async function writeToFirestore(payload) {
  if (typeof window === 'undefined') return;
  if (!firestoreWriterPromise) {
    firestoreWriterPromise = import('../admin/firebase/firestore').then(m => m.recordProgressEvent);
  }
  try {
    const recordProgressEvent = await firestoreWriterPromise;
    await recordProgressEvent(payload.code, {
      type: 'PROGRESS',
      milestone: payload.milestone,
      data: payload.data || {},
      cohortId: payload.cohortId || null,
      studentSeq: payload.studentSeq || null,
      at: payload.at ? new Date(payload.at) : new Date(),
      source: payload.source || 'semi-edu',
    });
  } catch { /* Firebase 미설정/오프라인이면 조용히 실패 — postMessage 폴백 사용 */ }
}

/**
 * Day 완료 신호 — progress-tracker.completeDay 안에서 자동 호출됨.
 */
export function reportDayComplete(day, completedModules = []) {
  reportEvent(`day${day}:complete`, { day, completedModules });
}

/**
 * Day별 체류 시간 누적 — useTimeTracker 훅이 unmount 시 호출.
 */
export function reportTimeSpent(day, timeMs) {
  if (!timeMs || timeMs < 1000) return;
  reportEvent(`day${day}:time`, { day, timeMs });
}

/**
 * 퀴즈 완료 — 시뮬레이터의 quizCompleted 시점에 호출.
 * day는 모르고 module만 알면 됨 (코스마다 day 매핑이 달라서).
 */
export function reportQuiz(moduleName, { score, maxScore, attempts = 1, userAnswers }) {
  reportEvent(`module:${moduleName}:quiz`, {
    module: moduleName,
    score,
    maxScore,
    attempts,
    userAnswers,
  });
}

/**
 * 트러블슈팅 시나리오 결과 — success/fail 시 호출.
 */
export function reportTroubleshoot(moduleName, { scenario, success, elapsedMs, actionsSequence }) {
  reportEvent(`module:${moduleName}:troubleshoot`, {
    module: moduleName,
    scenario,
    success,
    elapsedMs,
    actionsSequence,
  });
}

// ── 기관 좌석 라이브 상태(하트비트) ──────────────────────────────
// 이벤트(progress/{code}/events)로 쌓지 않고 좌석 문서 1건을 in-place 갱신한다.
// → 30명이 종일 학습해도 문서 폭증/읽기 비용 없이 실시간 모니터링 가능.
//
// 누적값(activeMs/locationMs)은 절대값 덮어쓰기가 아니라 increment(델타)로 더한다.
// (덮어쓰면 새 세션이 0으로 시작해 이전 누적을 지워버린다 — 총 체류시간 리셋 버그.)
// 규칙(seats.update)이 허용하는 키만 쓴다: lastSeenAt/activeMs/location/
// locationLabel/locationMs/locationLabels.
let seatWriterPromise = null;
function loadSeatWriter() {
  if (!seatWriterPromise) {
    seatWriterPromise = Promise.all([
      import('../admin/firebase/config'),
      import('firebase/firestore'),
    ]);
  }
  return seatWriterPromise;
}

/**
 * 좌석 engagement 라이브 갱신.
 * @param {object} p
 * @param {number} p.deltaMs       직전 flush 이후 늘어난 활성시간(ms). >0 이면 activeMs 에 increment.
 * @param {string} p.locId         현재 위치 id (top-level 'location' 에 기록)
 * @param {string} p.locLabel      현재 위치 표시 라벨
 * @param {object} p.locDeltas     { sanitizedKey: { ms, label } } 위치별 증가분
 */
export async function flushSeatEngagement({ deltaMs = 0, locId, locLabel, locDeltas = {} }) {
  const license = getLicense();
  const code = license?.code ? String(license.code).toUpperCase() : '';
  if (!code.startsWith('CLS-')) return; // 기관 좌석만 — 개인/체험은 좌석 없음
  if (typeof window === 'undefined') return;
  try {
    const [{ db }, fs] = await loadSeatWriter();
    if (!db) return;
    const update = {
      lastSeenAt: fs.serverTimestamp(),
      location: locId || 'home',
      locationLabel: locLabel || '',
    };
    if (deltaMs > 0) update.activeMs = fs.increment(deltaMs);
    Object.entries(locDeltas).forEach(([key, { ms, label }]) => {
      if (ms > 0) {
        update[`locationMs.${key}`] = fs.increment(ms);
        update[`locationLabels.${key}`] = label || key;
      }
    });
    await fs.updateDoc(fs.doc(db, 'seats', code), update);
  } catch { /* 권한/오프라인 → 조용히 실패 */ }
}
